/**
 * External Imports
 */
import { FC, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { useUserRolesRef } from '../../../services/UserRoles';
import {
  logCOGReportClick,
  logHRInsightsClick,
  logTableStart
} from '../../../utils/analyticsHelpers';
import { setLastPath } from '../../../utils/helpers';

/**
 * Styling Imports
 */
import {
  IconContainer,
  InlineRelativeContainer,
  PopItem,
  PopContent,
  ReportDropDownContainer,
  StyledNavTab
} from './NavDropdown.styles';

const ReportDropdown: FC<{}> = () => {
  const [reportDropdownState, setReportDropdownState] = useState(false);
  const [, setShowPopover] = useState(false);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logTrackingEvent } = useAnalyticsApi();

  const {
    isEnterpriseAdmin,
    isPatientRounder,
    isEmployeeRounder,
    isCSCRounder,
    isCNEdRounder,
    isNonSupportRounder,
    isAnyValidationRounder
  } = useUserRolesRef();

  return (
    <>
      <StyledNavTab
        id="Reports-Tab"
        unit="tab"
        active={pathname.includes('reports')}
        onBlur={() => setReportDropdownState(false)}
        onClick={() => setReportDropdownState(!reportDropdownState)}
      >
        <IconContainer>
          Reports
          <NeuIcon color="gray" size="1x">
            arrow_drop_down
          </NeuIcon>
        </IconContainer>
      </StyledNavTab>
      <InlineRelativeContainer onBlur={() => setShowPopover(false)}>
        <ReportDropDownContainer showReportDropdown={reportDropdownState}>
          {(isEnterpriseAdmin || isEmployeeRounder) && (
            <PopItem
              className="dropdown-hover-item"
              role="option"
              tabIndex={0}
              onMouseDown={() => {
                window.open(
                  'https://hrinsights.medcity.net/analytics/emp-rounding',
                  '_blank'
                );
                logTrackingEvent(logHRInsightsClick());
              }}
            >
              <PopContent>
                HR Insights{' '}
                <NeuIcon color="blue" size="1x">
                  launch
                </NeuIcon>
              </PopContent>
            </PopItem>
          )}

          {(isEnterpriseAdmin || isCSCRounder) && (
            <PopItem
              className="dropdown-hover-item"
              role="option"
              tabIndex={0}
              onMouseDown={() =>
                window.open(
                  'https://app.powerbi.com/groups/me/apps/1384df98-12c7-4007-a470-a692392a8f60/reports/54e09b8c-a85a-4120-9fa4-c8f8df8daa1d?ctid=335a6d75-c3eb-4b1a-ac08-d49338816ca0&experience=power-bi',
                  '_blank'
                )
              }
            >
              <PopContent>
                CSC Reports{' '}
                <NeuIcon color="blue" size="1x">
                  launch
                </NeuIcon>
              </PopContent>
            </PopItem>
          )}

          {(isEnterpriseAdmin || isCNEdRounder) && (
            <PopItem
              className="dropdown-hover-item"
              role="option"
              tabIndex={0}
              onMouseDown={() =>
                window.open(
                  'https://app.powerbi.com/groups/me/apps/1384df98-12c7-4007-a470-a692392a8f60/reports/d661531f-b8f0-4235-8247-5d23010e9dfd/486cb9a5665417a91ed1?ctid=335a6d75-c3eb-4b1a-ac08-d49338816ca0&experience=power-bi',
                  '_blank'
                )
              }
            >
              <PopContent>
                CNEd Reports{' '}
                <NeuIcon color="blue" size="1x">
                  launch
                </NeuIcon>
              </PopContent>
            </PopItem>
          )}

          {(isEnterpriseAdmin || isNonSupportRounder) && (
            <>
              {(isEnterpriseAdmin || isPatientRounder) && (
                <>
                  <PopItem
                    className="dropdown-hover-item"
                    role="option"
                    tabIndex={0}
                    onMouseDown={() => {
                      window.open(
                        'https://www.powerbi.com/groups/me/apps/1384df98-12c7-4007-a470-a692392a8f60/reports/206e4c55-8696-467a-a80d-2d1a3d7ba420/ReportSection3c7d73a9f3f288b13269',
                        '_blank'
                      );
                      logTrackingEvent(logCOGReportClick());
                    }}
                  >
                    <PopContent>
                      COG Patient Reports{' '}
                      <NeuIcon color="blue" size="1x">
                        launch
                      </NeuIcon>
                    </PopContent>
                  </PopItem>
                  <PopItem
                    className="dropdown-hover-item"
                    role="option"
                    tabIndex={0}
                    onMouseDown={() => {
                      setLastPath('/reports/patient/summary');
                      navigate('/reports/patient/summary');
                      logTrackingEvent(
                        logTableStart('/reports/patient/summary')
                      );
                    }}
                  >
                    <PopContent>Patient Reports</PopContent>
                  </PopItem>
                </>
              )}
              {(isEnterpriseAdmin || isEmployeeRounder) && (
                <PopItem
                  className="dropdown-hover-item"
                  role="option"
                  tabIndex={0}
                  onMouseDown={() => {
                    setLastPath('/reports/employee/summary');
                    navigate('/reports/employee/summary');
                    logTrackingEvent(
                      logTableStart('/reports/employee/summary')
                    );
                  }}
                >
                  <PopContent>Employee Reports</PopContent>
                </PopItem>
              )}
            </>
          )}

          {(isEnterpriseAdmin || isAnyValidationRounder) && (
            <PopItem
              className="dropdown-hover-item"
              role="option"
              tabIndex={0}
              onMouseDown={() =>
                window.open(
                  'https://app.powerbi.com/groups/me/apps/1384df98-12c7-4007-a470-a692392a8f60/reports/1f19deba-6217-447a-8edf-492de4d41a94/74233e32cd9f76d5dc1e?ctid=335a6d75-c3eb-4b1a-ac08-d49338816ca0&experience=power-bi',
                  '_blank'
                )
              }
            >
              <PopContent>
                Validation Reports
                <NeuIcon color="gray" size="1x">
                  launch
                </NeuIcon>
              </PopContent>
            </PopItem>
          )}
        </ReportDropDownContainer>
      </InlineRelativeContainer>
    </>
  );
};

export default ReportDropdown;
